import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { updateServiceTaxPercentage } from '@/modules/Service/services'
import { Service } from '@/modules/Service/types'
import { ModalProps } from '@/types/shared'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm } from 'vnetwork-ui'
import { QUERY_KEY_COMBINATIONS } from '@/constants/reactQueryKeys'

export interface EditServiceTaxPercentageProps extends ModalProps {
    serviceID: number
    service: Service
}

interface FormValues {
    service_tax_percentage: number
    note?: string
}

const EditServiceTaxPercentage: React.FC<EditServiceTaxPercentageProps> = ({
    open,
    onClose,
    service,
    serviceID,
}) => {
    const { t } = useTranslation()
    const { refetch } = useQueryClientCallback({
        pattern: 'services',
        mode: 'contains',
    })
    const { refetch: refetchTickets } = useQueryClientCallback({
        pattern: 'tickets',
        mode: 'contains',
    })
    const formControl = useForm<FormValues>({
        defaultValues: {
            service_tax_percentage: service?.service_tax_percentage || 0,
            note: '',
        },
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: updateServiceTaxPercentage,
        },
        {
            successMessage: t('update-service-tax-percentage-success'),
            errorMessage: t('error-update-service-tax-percentage'),
            reactQueryKeys: QUERY_KEY_COMBINATIONS.SERVICE_WITH_TICKET,
            onSuccess: () => {
                refetch()
                refetchTickets()
                onClose()
            },
        }
    )

    const handleSubmit = async (values: FormValues) => {
        mutateAsync({
            serviceID,
            service_tax_percentage: values.service_tax_percentage,
            note: values.note,
        })
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('edit-service-tax-percentage')}
        >
            <GridHookForm
                onSubmit={handleSubmit}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        handler: onClose,
                        disabled: isPending,
                        variant: 'outlined',
                    },
                    {
                        title: t('submit'),
                        type: 'submit',
                        disabled: isPending,
                        loading: isPending,
                        variant: 'contained',
                    },
                ]}
                data={[
                    {
                        formItemProps: {
                            name: 'service_tax_percentage',
                            type: 'number',
                            label: t('service-tax-percentage'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'note',
                            type: 'textarea',
                            label: t('note'),
                        },
                        className: 'col-span-4',
                    },
                ]}
                formControl={formControl}
            />
        </Dialog>
    )
}

export default EditServiceTaxPercentage
